'use client'

import { useEffect, useState } from 'react'
import { captureEvent, EVENTS } from '@/lib/posthog/events'
import { createClient } from '@/lib/supabase/client'
import { getSiteURL } from '@/utils/siteUrl'
import { Eye, EyeOff } from 'lucide-react'
import Link from 'next/link'
import { CaptchaWidget } from '@/components/client/CaptchaWidget'
import { useCaptcha } from '@/hooks/useCaptcha'
import { setOAuthIntent } from '@/lib/auth/oauth-intent.client';
import { storeSlipDestination, type SlipDestination } from '@/utils/slipDestinationStorage'
import { signInOrOutHere } from '@/lib/auth/thisTab'

interface LoginFormProps {
  redirectTo: string
  /** Shared-slip destination from `/s/<id>`'s sign-in link. Persisted to
   *  localStorage on mount so `PlayerSelectionView`'s `useSlipDeepLink`
   *  consumer can open the overlay anchored to the shared player after
   *  auth completes. Survives the email-login page reload and the OAuth
   *  round-trip because localStorage is browser-local. */
  slipDestination?: SlipDestination | null
}

export default function LoginForm({ redirectTo, slipDestination }: LoginFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const captcha = useCaptcha()
  const supabase = createClient()

  // Mirrors SignUpForm's slip-destination persistence — keeps the
  // share-page deep-link flow uniform for both new and existing users.
  useEffect(() => {
    if (slipDestination) storeSlipDestination(slipDestination)
  }, [slipDestination])

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    captureEvent(EVENTS.LOGIN_SUBMITTED, { method: 'email' })

    try {
      const captchaToken = await captcha.getToken()
      const { error } = await signInOrOutHere(() => supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      }))

      if (error) {
        const isCaptchaError =
          error.code === 'captcha_failed' ||
          (error.code === 'unexpected_failure' &&
            error.message?.toLowerCase().includes('captcha'))
        captureEvent(EVENTS.LOGIN_FAILED, {
          method: 'email',
          reason: isCaptchaError ? 'captcha_failed' : (error.code ?? 'unknown'),
        })
        if (isCaptchaError) {
          console.warn('[captcha] verification failed', {
            code: error.code,
            message: error.message?.slice(0, 120),
          })
          setError('Bot check failed — please click Sign in again.')
        } else {
          setError(error.message)
        }
      } else {
        captureEvent(EVENTS.LOGIN_SUCCEEDED, { method: 'email' })
        window.location.href = redirectTo
      }
    } catch (_err) {
      captureEvent(EVENTS.LOGIN_FAILED, { method: 'email', reason: 'exception' })
      setError('An unexpected error occurred')
    } finally {
      // Remount the widget so a retry gets a fresh single-use token (the prior
      // one was consumed by Supabase, even on a wrong-password attempt).
      captcha.refresh()
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    if (loading) return; // Prevent double-click

    setError(null)
    setLoading(true)
    captureEvent(EVENTS.LOGIN_SUBMITTED, { method: 'google' })

    try {
      // Cookie-based intent — survives the Google→Supabase→callback roundtrip with
      // SameSite=Lax. A query string on the callback URL would be stripped by Supabase's
      // strict OAuth redirect-URI allowlist. See lib/auth/oauth-intent.
      setOAuthIntent(redirectTo);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${getSiteURL()}/auth/callback`,
        },
      })

      if (error) {
        captureEvent(EVENTS.LOGIN_FAILED, { method: 'google', reason: error.code ?? 'unknown' })
        setError(error.message)
        setLoading(false)
      }
      // If successful, user will be redirected to Google
    } catch (_err) {
      captureEvent(EVENTS.LOGIN_FAILED, { method: 'google', reason: 'exception' })
      setError('An unexpected error occurred')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg dark:bg-bg-primary flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Login Card */}
        <div className="bg-white dark:bg-bg-secondary rounded-lg shadow-lg p-8 border border-border dark:border-border-secondary">
          {/* Logo */}
          <div className="text-center mb-6">
            <Link href="/">
              <img
                src="/sharpr.png"
                alt="Sharpr"
                className="h-12 w-auto mx-auto"
              />
            </Link>
          </div>
          {/* Error Message */}
          {error && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
            >
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 mb-6 bg-white dark:bg-bg-tertiary text-fg dark:text-text-primary border border-border dark:border-border-secondary rounded-md hover:bg-hover dark:hover:bg-bg-overlay transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border dark:border-border-secondary"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-bg-secondary text-muted-foreground dark:text-text-tertiary">
                Or continue with email
              </span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-fg dark:text-text-primary mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="w-full px-3 py-2 bg-white dark:bg-bg-tertiary border border-border dark:border-border-secondary rounded-md text-fg dark:text-text-primary placeholder:text-muted-foreground dark:placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-200"
                placeholder="you@example.com"
                disabled={loading}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-fg dark:text-text-primary"
                >
                  Password
                </label>
                <Link
                  href="/forgot-password"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  spellCheck={false}
                  className="w-full px-3 py-2 pr-10 bg-white dark:bg-bg-tertiary border border-border dark:border-border-secondary rounded-md text-fg dark:text-text-primary placeholder:text-muted-foreground dark:placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-200"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground dark:text-text-tertiary hover:text-fg dark:hover:text-text-primary transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-center">
              <CaptchaWidget
                key={captcha.renderKey}
                ref={captcha.ref}
                onSuccess={captcha.setToken}
                onExpire={captcha.refresh}
                onError={captcha.refresh}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground dark:text-text-secondary">
              Don't have an account?{' '}
              <Link
                href="/signup"
                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
